/**
 * ModuleGuard — Phase 75: Multi-Tenant Module Access Guard
 *
 * ─── Problem ────────────────────────────────────────────────────────────────
 * Eli identified a multi-tenant authorization leak.  The existing
 * <RequirePermission module="..."> guard checks whether the user has access
 * to a module — but only via `permissions.activeModules`, which is sourced
 * from `companyContext.activeCompanyId`.  When the user belongs to MULTIPLE
 * companies, only ONE of which is licensed for the target module, two bad
 * things happen:
 *
 *   1. If the active company is licensed → the guard passes and the page
 *      loads — even when the user intuitively expected the OTHER company.
 *
 *   2. If the active company is NOT licensed → the guard redirects to /403
 *      or `redirectTo`, which in our App.tsx maps the un-matched fallthrough
 *      to `/` (public landing).  The user is dropped completely out of the
 *      app on a routine cross-module click.
 *
 * The pre-existing `RequirePermission` is fine for plan-tier / role gates,
 * but does NOT understand the "switch to another company that DOES have
 * this module" flow.
 *
 * ─── Behaviour ──────────────────────────────────────────────────────────────
 * Wrap a module shell route like so:
 *
 *   <Route path="/app/inventory" element={
 *     <ModuleGuard module="inventory">
 *       <InventoryLayout />
 *     </ModuleGuard>
 *   }>
 *
 * On entry the guard:
 *
 *   1. Waits while CompanyContext is still loading (no decision yet).
 *   2. Super-admins always pass (consistent with `can()` semantics).
 *   3. Reads the active company's `active_modules` array.
 *      • If the module IS in it → renders children, decision done.
 *
 *   4. Otherwise scans `companies[]` for any OTHER company licensed for
 *      this module.
 *      • If exactly one such target exists, or one is clearly preferable
 *        (deterministic pick: first match in the order returned by the
 *        CompanyContext), the guard:
 *          a. Calls `setActiveCompanyId(targetId)` once.
 *          b. Fires a subtle "info" toast in Hebrew telling the user
 *             which company we switched to.
 *          c. Re-evaluates on the next render — at which point the new
 *             active company satisfies the gate and children render.
 *
 *   5. If no licensed company is available, renders the dedicated
 *      <UnauthorizedModulePage /> INSIDE the current app shell rather
 *      than redirecting away.  The page exposes:
 *          • a "switch company" dropdown of all the user's other
 *            companies (with a lock icon on each unlicensed one);
 *          • a "back to a working module" button that links to the
 *            first module the active company DOES have licensed
 *            (cashflow → inventory → hr, in that precedence).
 *
 * ─── Design notes ──────────────────────────────────────────────────────────
 * • The guard is intentionally pure-render: a single state slice
 *   (`autoSwitched`) prevents an infinite loop if the freshly-switched
 *   company is itself somehow unlicensed (data race during the same render).
 * • Super-admin bypass is matched to `rbac.can()` so SAs never get stuck.
 * • The guard does NOT replace per-route, per-level guards
 *   (`<RequirePermission level="editor">`) — those still wrap individual
 *   leaf routes inside the module shell.  This guard only governs MODULE
 *   ENTRY, which is exactly the surface Eli's bug exploits.
 */

import { useEffect, useRef, type ReactNode }  from 'react';
import { RefreshCw }                          from 'lucide-react';
import { useCompany }                         from '@/context/CompanyContext';
import { UnauthorizedModulePage }             from '@/pages/UnauthorizedModulePage';
import { type AppModule }                     from '@/lib/rbac';
import {
  useHasModule,
  usePhase76Auth,
  usePhase76Hydrated,
}                                              from '@/lib/auth';

interface ModuleGuardProps {
  /** The module that the wrapped route shell requires. */
  module:   AppModule;
  children: ReactNode;
}

/**
 * Picks the deterministic "preferred" target company licensed for `module`.
 * Returns null when no other company satisfies the gate.
 *
 * Phase 76.2.E: this helper now reads EXCLUSIVELY from the Phase 76 context
 * surface — `usePhase76Auth().companies` (MyCompany[] from
 * `get_my_companies_with_modules()`) and the `hasModuleInCompany(id, key)`
 * cached lookup populated from `company_module_subscriptions_v76`. The
 * legacy `companies[].active_modules` read has been fully retired.
 */
export function ModuleGuard({ module, children }: ModuleGuardProps) {
  const phase76Hydrated = usePhase76Hydrated();
  const phase76HasMod   = useHasModule(module);

  usePhase76Auth();

  const { activeCompany, loadingCompanies } = useCompany();

  const switchedRef = useRef<{ module: AppModule; companyId: string } | null>(null);

  // ── Compute decision purely from data ─────────────────────────────────
  // Phase 76 authoritative check for the active company. With the
  // `subscribedModulesByCompany` cache now hydrated for ALL companies during
  // the main refresh pass (Phase 76.2.E), `useHasModule(module)` is the
  // single source of truth once `phase76Hydrated` is true.
  const isActiveLicensed = phase76Hydrated ? phase76HasMod : false;

  // No auto-switch: we always show UnauthorizedModulePage when the active
  // company is not licensed. The user can manually switch via that page.
  useEffect(() => {
    if (isActiveLicensed) switchedRef.current = null;
  }, [isActiveLicensed]);

  // ── Render decisions ──────────────────────────────────────────────────

  // 1. Still loading → spinner
  if (loadingCompanies || !phase76Hydrated) {
    return (
      <div className="flex items-center justify-center py-24 gap-3 text-gray-400">
        <RefreshCw className="w-5 h-5 animate-spin" />
        <span className="text-sm">טוען הרשאות חברה…</span>
      </div>
    );
  }

  // 2. Active company is licensed → render (super-admin included — no bypass)
  if (isActiveLicensed) return <>{children}</>;

  // 3. Company not licensed for this module → show unauthorized page
  return (
    <UnauthorizedModulePage
      module={module}
      activeCompanyName={activeCompany?.legal_name ?? null}
    />
  );
}
